import {
  VCL_BASELINE_STEPS,
  VCL_CONTENT_MAX_BASELINE,
  VCL_CONTROL_GEOMETRY_BASELINE,
  VCL_CONTROL_RADIUS_BASELINE,
  VCL_ELEVATION_BASELINE,
  VCL_GAP_BASELINE,
  VCL_PAGE_GUTTER_BASELINE,
  VCL_STACK_BASELINE,
  VCL_SURFACE_PAD_BASELINE,
  VCL_SURFACE_RADIUS_BASELINE,
  VCL_TYPE_BASELINE,
} from './baseline.js';
import { getVclControl, VCL_CONTROLS } from './controls.js';
import { assertCustomerVclSurface } from './surfaces.js';
import type {
  MutableVclControlState,
  SurfaceId,
  VclClampNote,
  VclControlId,
  VclControlState,
  VclEffectiveResult,
} from './types.js';
import { VCL_CONTROL_IDS, VCL_TYPE_ROLES } from './types.js';

const DATA_ATTR = 'data-customer-vcl';
const REM_STEP = 0.0625;
const PX_STEP = 2;

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function rem(n: number): string {
  const rounded = Math.round(n * 1000) / 1000;
  return `${rounded}rem`;
}

function px(n: number): string {
  return `${Math.round(n)}px`;
}

/**
 * Range-clamp each control to its declared min/max, then apply invariant clamps (G17).
 * Export / panel must use this effective state - never weaken invariants for aesthetics.
 */
export function getEffectiveState(requested: VclControlState): VclEffectiveResult {
  const state: MutableVclControlState = { ...requested };
  const clamps: VclClampNote[] = [];

  for (const def of VCL_CONTROLS) {
    const raw = requested[def.id];
    const ranged = clampNumber(raw, def.min, def.max);
    if (ranged !== raw) {
      clamps.push({
        controlId: def.id,
        reason: `Outside control range [${def.min}, ${def.max}]`,
        requested: raw,
        effective: ranged,
      });
    }
    state[def.id] = ranged;
  }

  // C2/C3 - never go below clearance floor step (−2 already range-capped; note compact audit).
  // Additional semantic floors are enforced in CSS var floors below.

  // C4 - if extreme would imply heights under 44/48, keep step but CSS uses max(); note for panel.
  const c4 = state.C4;
  const mdAfter = VCL_CONTROL_GEOMETRY_BASELINE.heightMdPx + c4 * PX_STEP;
  const lgAfter = VCL_CONTROL_GEOMETRY_BASELINE.heightLgPx + c4 * PX_STEP;
  if (mdAfter < VCL_CONTROL_GEOMETRY_BASELINE.minHitPx || lgAfter < VCL_CONTROL_GEOMETRY_BASELINE.minCtaHeightPx) {
    clamps.push({
      controlId: 'C4',
      reason: 'Heights clamped via max() to 44×44 / CTA ≥48 (EC-01)',
      requested: c4,
      effective: c4,
    });
  }

  // C5 ≠ safe-area - reject any attempt to encode safe-area into C5 (state stays aesthetic pad only).
  // Encoded as a permanent note when C5 is non-baseline so operators see the must-not.
  if (state.C5 !== VCL_BASELINE_STEPS.C5) {
    clamps.push({
      controlId: 'C5',
      reason: 'C5 applies surface pad only - never bound to env(safe-area-inset-*)',
      requested: state.C5,
      effective: state.C5,
    });
  }

  // C8 - context min widths enforced in CSS builder; note if requested step would undershoot.
  const shellRem =
    VCL_CONTENT_MAX_BASELINE.shellRem + state.C8 * 2;
  if (shellRem < VCL_CONTENT_MAX_BASELINE.minShellRem) {
    clamps.push({
      controlId: 'C8',
      reason: `Shell content-max clamped to ≥ ${VCL_CONTENT_MAX_BASELINE.minShellRem}rem`,
      requested: state.C8,
      effective: state.C8,
    });
  }

  // C9 - token is rem-only; CSS `.customer-safe-area-x` preserves max(token, safe-area) per side.
  if (state.C9 !== VCL_BASELINE_STEPS.C9) {
    clamps.push({
      controlId: 'C9',
      reason: 'Gutter token rem-only; CSS max(token, env(safe-area-inset-*)) per side; dvh/keyboard preserved',
      requested: state.C9,
      effective: state.C9,
    });
  }

  // C6/C7/C10 - reject “round/elevate everything” extremes beyond declared max (already ranged).
  // C7 must not mirror C6 automatically - independent steps already ensure this.

  const effectiveState = state as VclControlState;
  return {
    state: effectiveState,
    clamps,
    // Post-clamp rem/px map (C8 floors via contentMaxVars Math.max mins).
    cssVars: buildVclCssVars(effectiveState),
  };
}

function typeVars(step: number): Record<string, string> {
  // G3: CSS role set only; per-role size delta - NOT a universal multiplier (C1 mustNot).
  const out: Record<string, string> = {};
  for (const role of VCL_TYPE_ROLES) {
    const base = VCL_TYPE_BASELINE[role];
    const size = Math.max(0.75, base.sizeRem + step * REM_STEP);
    out[`--vcl-type-${role}-size`] = rem(size);
    out[`--vcl-type-${role}-weight`] = String(base.weight);
    // Frozen lineHeightCss - step 0 writes exact CSS defaults (G2 identity).
    out[`--vcl-type-${role}-line`] = base.lineHeightCss;
    out[`--vcl-type-${role}-tracking`] = `${base.trackingEm}em`;
  }
  return out;
}

function stackVars(step: number): Record<string, string> {
  const floor = { xs: 0.125, sm: 0.25, md: 0.5, lg: 0.75 };
  const keys = ['xs', 'sm', 'md', 'lg'] as const;
  const out: Record<string, string> = {};
  for (const key of keys) {
    const raw = VCL_STACK_BASELINE[key] + step * REM_STEP;
    out[`--vcl-space-stack-${key}`] = rem(Math.max(floor[key], raw));
  }
  return out;
}

function gapVars(step: number): Record<string, string> {
  // Floors are mins for tuned negative steps only — below CSS baselines
  // (sm/md/lg = 0.5/0.75/1). Floor md 0.375 < baseline 0.75 does not fight step 0.
  // Keys match customer.extensions.css (sm|md|lg only — no orphan xs).
  const floor = { sm: 0.25, md: 0.375, lg: 0.5 };
  const keys = ['sm', 'md', 'lg'] as const;
  const out: Record<string, string> = {};
  for (const key of keys) {
    const raw = VCL_GAP_BASELINE[key] + step * REM_STEP;
    out[`--vcl-gap-inline-${key}`] = rem(Math.max(floor[key], raw));
  }
  return out;
}

function controlGeometryVars(step: number): Record<string, string> {
  const g = VCL_CONTROL_GEOMETRY_BASELINE;
  // Step 0: rem/max() matching CSS defaults — no px overwrite drift (G2/G8).
  if (step === 0) {
    return {
      '--vcl-control-height-md': '2.5rem',
      '--vcl-control-height-lg': 'max(3rem, 48px)',
      '--vcl-control-pad-x': rem(g.padXMdRem),
      '--vcl-control-pad-x-md': rem(g.padXMdRem),
      '--vcl-control-pad-x-lg': rem(g.padXLgRem),
      '--vcl-control-min-hit': '2.75rem',
      '--vcl-control-min-cta-height': 'max(3rem, 48px)',
    };
  }
  const md = Math.max(g.minHitPx, g.heightMdPx + step * PX_STEP);
  const lg = Math.max(g.minCtaHeightPx, g.heightLgPx + step * PX_STEP);
  const padX = rem(g.padXMdRem + step * (REM_STEP / 2));
  return {
    '--vcl-control-height-md': px(md),
    '--vcl-control-height-lg': px(lg),
    '--vcl-control-pad-x': padX,
    '--vcl-control-pad-x-md': padX,
    '--vcl-control-pad-x-lg': rem(g.padXLgRem + step * (REM_STEP / 2)),
    '--vcl-control-min-hit': px(g.minHitPx),
    '--vcl-control-min-cta-height': px(g.minCtaHeightPx),
  };
}

function surfacePadVars(step: number): Record<string, string> {
  const floor = 0.5;
  return {
    '--vcl-surface-pad-sm': rem(Math.max(floor, VCL_SURFACE_PAD_BASELINE.smRem + step * REM_STEP)),
    '--vcl-surface-pad-md': rem(Math.max(floor, VCL_SURFACE_PAD_BASELINE.mdRem + step * REM_STEP)),
    '--vcl-surface-pad-lg': rem(Math.max(floor, VCL_SURFACE_PAD_BASELINE.lgRem + step * REM_STEP)),
  };
}

function controlRadiusVars(step: number): Record<string, string> {
  const b = VCL_CONTROL_RADIUS_BASELINE;
  // Step 0: CSS alias form ≡ customer.extensions.css (zero-delta).
  if (step === 0) {
    return {
      '--vcl-control-radius-sm': 'var(--radius-sm)',
      '--vcl-control-radius-md': 'var(--radius-md)',
      '--vcl-control-radius-lg': 'var(--radius-lg)',
    };
  }
  const sm = Math.max(0.25, Math.min(0.5, b.smRem + step * REM_STEP));
  return {
    '--vcl-control-radius-sm': rem(sm),
    '--vcl-control-radius-md': rem(Math.max(0.375, b.mdRem + step * REM_STEP)),
    '--vcl-control-radius-lg': rem(Math.max(0.5, b.lgRem + step * REM_STEP)),
  };
}

function surfaceRadiusVars(step: number): Record<string, string> {
  const b = VCL_SURFACE_RADIUS_BASELINE;
  // C7 independent of C6; Card binds `--vcl-surface-radius-xl`.
  // Step 0: md/lg/xl keep CSS alias form; sm has no CSS default (extra unused var).
  if (step === 0) {
    return {
      '--vcl-surface-radius-sm': rem(b.smRem),
      '--vcl-surface-radius-md': 'var(--radius-md)',
      '--vcl-surface-radius-lg': 'var(--radius-lg)',
      '--vcl-surface-radius-xl': 'var(--radius-xl)',
    };
  }
  return {
    '--vcl-surface-radius-sm': rem(Math.max(0.375, b.smRem + step * REM_STEP)),
    '--vcl-surface-radius-md': rem(Math.max(0.375, b.mdRem + step * REM_STEP)),
    '--vcl-surface-radius-lg': rem(Math.max(0.5, b.lgRem + step * REM_STEP)),
    '--vcl-surface-radius-xl': rem(Math.max(0.75, b.xlRem + step * REM_STEP)),
  };
}

function contentMaxVars(step: number): Record<string, string> {
  const b = VCL_CONTENT_MAX_BASELINE;
  const delta = step * 2;
  return {
    '--vcl-content-max-shell': rem(Math.max(b.minShellRem, b.shellRem + delta)),
    '--vcl-content-max-standard': rem(Math.max(b.minStandardRem, b.standardRem + delta)),
    '--vcl-content-max-wide': rem(Math.max(b.minWideRem, b.wideRem + delta)),
    '--vcl-content-max-narrow': rem(Math.max(b.minNarrowRem, b.narrowRem + delta)),
    '--vcl-content-max-auth': rem(Math.max(b.minAuthRem, b.authRem + delta * 0.5)),
    '--vcl-auth-field-stack-width': `${b.authFieldStackFraction * 100}%`,
  };
}

function pageGutterVars(step: number): Record<string, string> {
  // Token stays baseline rem only — do NOT pack env(safe-area-inset-*) into the shared gutter.
  // `.customer-safe-area-x` applies max(token, env(left)) / max(token, env(right)) separately.
  const token = rem(Math.max(0.5, VCL_PAGE_GUTTER_BASELINE.tokenRem + step * REM_STEP));
  return {
    '--vcl-page-gutter-x': token,
  };
}

function elevationVars(step: number): Record<string, string> {
  const b = VCL_ELEVATION_BASELINE;
  // G10: step 0 keeps soft theme-aware aliases (not rgba literals).
  if (step <= 0) {
    return {
      '--vcl-elevation-card': 'var(--shadow-card)',
      '--vcl-elevation-popover': 'var(--shadow-popover)',
    };
  }
  if (step === 1) {
    return {
      '--vcl-elevation-card': b.cardStrong,
      '--vcl-elevation-popover': b.popoverStrong,
    };
  }
  return {
    '--vcl-elevation-card': b.cardMax,
    '--vcl-elevation-popover': b.popoverMax,
  };
}

/** Build post-clamp CSS custom properties for the effective state. */
export function buildVclCssVars(effective: VclControlState): Record<string, string> {
  return {
    ...typeVars(effective.C1),
    ...stackVars(effective.C2),
    ...gapVars(effective.C3),
    ...controlGeometryVars(effective.C4),
    ...surfacePadVars(effective.C5),
    ...controlRadiusVars(effective.C6),
    ...surfaceRadiusVars(effective.C7),
    ...contentMaxVars(effective.C8),
    ...pageGutterVars(effective.C9),
    ...elevationVars(effective.C10),
  };
}

const KNOWN_VAR_PREFIX = '--vcl-';

function clearVclInlineVars(el: HTMLElement): void {
  const style = el.style;
  const toRemove: string[] = [];
  for (let i = 0; i < style.length; i += 1) {
    const name = style.item(i);
    if (name !== null && name.startsWith(KNOWN_VAR_PREFIX)) {
      toRemove.push(name);
    }
  }
  for (const name of toRemove) {
    style.removeProperty(name);
  }
}

export interface ApplyVclCssVarsOptions {
  readonly surface?: SurfaceId;
  /** When false, clear dataset/vars (VCL off). Default true. */
  readonly enabled?: boolean;
}

/**
 * Apply effective VCL state to an HTMLElement (typically `document.documentElement`).
 * Guards: customer surface only; clamp/reject beyond C4 44×48, C2/C3 floors, C5≠safe-area, C8 mins, C9 max(token,safe-area).
 */
export function applyVclCssVars(
  el: HTMLElement,
  requested: VclControlState,
  options: ApplyVclCssVarsOptions = {},
): VclEffectiveResult {
  const surface = options.surface ?? 'customer';
  assertCustomerVclSurface(surface);

  if (options.enabled === false) {
    el.removeAttribute(DATA_ATTR);
    clearVclInlineVars(el);
    return getEffectiveState(VCL_BASELINE_STEPS);
  }

  const result = getEffectiveState(requested);
  const vars = buildVclCssVars(result.state);

  el.setAttribute(DATA_ATTR, '1');
  clearVclInlineVars(el);
  for (const [name, value] of Object.entries(vars)) {
    el.style.setProperty(name, value);
  }

  return result;
}

/** CSS vars for a single control (promotion export helpers). */
export function buildCssVarsForControl(
  controlId: VclControlId,
  effective: VclControlState,
): Record<string, string> {
  void getVclControl(controlId);
  const all = buildVclCssVars(effective);
  const prefixes: Record<VclControlId, string[]> = {
    C1: ['--vcl-type-'],
    C2: ['--vcl-space-stack-'],
    C3: ['--vcl-gap-inline-'],
    C4: ['--vcl-control-height-', '--vcl-control-pad-', '--vcl-control-min-'],
    C5: ['--vcl-surface-pad-'],
    C6: ['--vcl-control-radius-'],
    C7: ['--vcl-surface-radius-'],
    C8: ['--vcl-content-max-', '--vcl-auth-field-stack-'],
    C9: ['--vcl-page-gutter-'],
    C10: ['--vcl-elevation-'],
  };
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(all)) {
    if (prefixes[controlId].some((p) => name.startsWith(p))) {
      out[name] = value;
    }
  }
  return out;
}

export function listControlIds(): readonly VclControlId[] {
  return VCL_CONTROL_IDS;
}
